import axios from 'axios'
import VueRouter from 'vue-router'
import router from '@/router'
import * as types from './types'
import { shouldUpdate } from '@/helpers/system_updates'
import { TWO_FACTOR_OK, TWO_FACTOR_REQUIRED, isPnRole } from '@/constants/auth'
import * as appointmentTypes from '@/modules/appointment/store/types'
import { isReportLockFresh } from './report_lock'

// Checagem da trava em andamento (não forçada) — o guard e várias telas podem pedir ao mesmo tempo.
let reportLockRequest = null

// Navegação que o guard redirecionou (ex.: PN com report pendente → central de pendências), cancelou ou que já
// estava na rota não é erro: o `push` rejeita com uma NavigationFailure, mas o login/perfil deu certo. Sem isso o
// LOGIN rejeitaria e a tela de login mostraria "credenciais inválidas" para o PN travado.
const ignoreNavigationFailure = error => {
  if (!VueRouter.isNavigationFailure(error)) throw error
}

export default {
  [types.GET_USER_DATA_CREDENTIALS]: ({ commit, dispatch }, payload) => {
    return axios.post('/api/users/login/by_token', payload)
      .then(resp => {
        commit(types.MUTATE_TOKEN, resp.data)
        dispatch(types.GET_USER_DATA, { hasRedirect: true })
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.LOGIN]: ({ commit, dispatch }, payload) => {
    return axios.post('/api/users/login', payload)
      .then(async resp => {
        const data = resp?.data || {}
        if (data?.status === TWO_FACTOR_REQUIRED) {
          return Promise.resolve(resp)
        }
        const anyToken = data?.token || data?.access_token || data?.jwt || data?.authorization?.token
        if (data?.status === TWO_FACTOR_OK || anyToken) {
          const normalized = anyToken ? { ...data, token: anyToken } : data
          commit(types.MUTATE_TOKEN, normalized)
          await dispatch(types.GET_USER_DATA, { hasRedirect: true })
          await router.push({ name: 'dashboard' }).catch(ignoreNavigationFailure)
          return Promise.resolve(resp)
        }
        return Promise.reject(data)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.RECOVER_PASSWORD]: ({ }, payload) => {
    return axios.post('/api/users/request_password', payload)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.SET_NEW_PASSWORD]: ({ }, payload) => {
    return axios.post('/api/users/new_password', payload)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.GET_USER_DATA]: ({ commit, dispatch }, payload) => {
    return axios.get('/api/users/profile', payload)
      .then(resp => {
        commit(types.MUTATE_TOKEN, resp.data)
        if (resp.data?.should_renew_token) {
          dispatch(types.RENEW_TOKEN)
        }
        const lastReleaseAt = new Date(resp.data?.latest_release_at)
        if (shouldUpdate(lastReleaseAt)) {
          window.location.href = window.location.pathname + '?ts=' + new Date().getTime();
        } else if (payload?.hasRedirect) {
          let params = {}
          const back_url = router.currentRoute.query?.back_url
          if (router.currentRoute.query?.id) {
            params.id = router.currentRoute.query?.id
          }
          if (router.currentRoute.query?.appointment) {
            params.appointment = router.currentRoute.query?.appointment
          }
          if (back_url) {
            router.push({
              name: back_url,
              params
            }).catch(ignoreNavigationFailure)
          }
        }
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.LOGOUT]: ({ commit }) => {
    return axios.post('/api/users/logout')
      .then(resp => {
        commit(types.MUTATE_TOKEN_LOGOUT)
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.GET_CUSTOMER_CREDENTIALS]: ({ commit }, payload) => {
    return axios.post('/api/login/by_customer_token', payload)
      .then(resp => {
        commit(types.MUTATE_TOKEN, resp.data)
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.SAVE_DARK_MODE]: ({ }, payload) => {
    return axios.patch('/api/users/dark_mode', payload)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.CHECK_CONTRACT_REPORT_PENDENCIES]: ({ }) => {
    return axios.get('/api/users/contract_report_pendencies')
      .then(resp => {
        if (resp.data?.id) {
          router.push({ name: 'contract-save', params: { 'blocked': true, 'appointment': resp.data.id } })
        }
      })
  },
  // Atualiza `state.reportLock` a partir do servidor e resolve com ele. Reaproveita a última checagem por
  // REPORT_LOCK_TTL_MS (30 s) e junta chamadas simultâneas; `{ force: true }` sempre consulta de novo
  // (depois de finalizar um report, na central). NUNCA rejeita: se a consulta falhar, resolve com o
  // último estado conhecido — o servidor continua aplicando a trava (423) e o guard tenta de novo na
  // próxima navegação.
  // Só PN é travado (mesma regra de `User::isPN()` no backend: tipo de contrato); para os demais não há
  // requisição, a menos que um 423 já tenha marcado a trava (o servidor é quem decide).
  [types.REFRESH_REPORT_LOCK]: ({ state, dispatch }, options) => {
    const force = !!(options && options.force)
    const lock = state.reportLock
    const applies = isPnRole(state.user && state.user.user_contract_type_id)
    if (!applies && !lock.locked) return Promise.resolve(lock)
    if (!force && isReportLockFresh(lock)) return Promise.resolve(lock)
    if (!force && reportLockRequest) return reportLockRequest

    const request = dispatch(appointmentTypes.GET_REPORT_PENDENCIES)
      .catch(() => null)
      .then(() => state.reportLock)
      .finally(() => {
        if (reportLockRequest === request) reportLockRequest = null
      })
    reportLockRequest = request

    return request
  },
  [types.CHECK_COMPANY_PENDENCIES]: ({ commit, state }) => {
    var today = new Date();
    if (state.company_pendencies_viewed === today.toDateString()) { return }
    return axios.get('/api/users/company_pendencies')
      .then(resp => {
        if (resp.data?.has_company_data_pendencies) {
          commit(types.MUTATE_COMPANY_PENDENCIES)
          router.push({
            name: 'consultant-company-info-save',
            params: {
              company_data_pendency: true,
              user: resp.data.user,
              id: resp.data.user.consultant_id
            }
          })
        } else if (resp.data?.has_company_contract_pendencies) {
          commit(types.MUTATE_COMPANY_PENDENCIES)
          router.push({
            name: 'consultant-company-info-save',
            params: {
              company_contract_pendency: true,
              user: resp.data.user,
              id: resp.data.user.consultant_id
            }
          })
        }
      })
  },
  [types.GET_APPIFY_LINK]: ({ }, payload) => {
    return axios.get('/api/users/link_appify', { params: payload })
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.CHECK_SYSTEM_VERSION_NOTIFICATIONS]: ({ commit }, payload) => {
    return axios.get('/api/users/announcement_notifications')
      .then(resp => {
        commit(types.MUTATE_SYSTEM_VERSION_NOTIFICATIONS, resp.data)
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.READ_SYSTEM_NOTIFICATION]: ({ commit }, payload) => {
    return axios.post(`/api/announcements/read_notification/${payload}`)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.RENEW_TOKEN]: ({ commit }, payload) => {
    return axios.get('/api/users/renew_token', payload)
      .then(resp => {
        commit(types.MUTATE_TOKEN, resp.data)
        window.location.reload(true)
      })
  },
  [types.CHECK_PN_ONBOARDING_STATUS]: ({ commit }) => {
    return axios.get('/api/consultants/my_onboarding_status')
      .then(resp => {
        commit(types.MUTATE_PN_ONBOARDING_STATUS, resp.data)
        return resp.data
      })
      .catch(() => null)
  },
  [types.DISMISS_PN_DOCUMENT_ALERT]: ({ commit }, day) => {
    commit(types.MUTATE_PN_DOCUMENT_ALERT_DISMISSED)
    return axios.post('/api/consultants/dismiss_document_alert', { day })
      .catch(() => null)
  },
}
